// Équipe d'une app : rôle admin (tout) ou équipe (statuts, édition).
import type { JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import type { Member, MemberRole } from '@feedbaacks/sdk'
import { Avatar, Button, errorMessage, Spinner, useFeedbaacks } from '@feedbaacks/ui'
import { d } from '../i18n'

export function Members({ appKey }: { appKey: string }): JSX.Element {
  const { client, notify, confirm } = useFeedbaacks()
  const [list, setList] = useState<Member[] | null>(null)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<MemberRole>('staff')
  const fail = (err: unknown) => notify('error', errorMessage(err, d('error_generic')))
  const load = () => client.apps.members.list(appKey).then(setList, fail)
  useEffect(() => {
    void load()
  }, [client, appKey])
  if (!list) return <div class="dash-center"><Spinner size={24} /></div>

  const setMember = (input: { email?: string; user_id?: string; role: MemberRole }) =>
    client.apps.members.set(appKey, input).then(load, fail)
  const remove = async (m: Member) => {
    if (!(await confirm({ title: d('members_remove_title'), message: d('members_remove_message', { name: m.name || m.email || m.user_id }), confirmLabel: d('members_remove'), destructive: true }))) return
    client.apps.members.remove(appKey, m.user_id).then(load, fail)
  }

  return (
    <div class="dash-page" data-testid="dash-members">
      <section class="dash-card">
        <ul class="dash-list">
          {list.map((m) => (
            <li key={m.user_id} data-testid="dash-member">
              <Avatar name={m.name || m.email || '?'} src={m.avatar_url ?? undefined} />
              <span class="fb-strong">{m.name || m.email}</span>
              <span class="fb-faint">{m.email}</span>
              <span class="fb-grow" />
              <select class="fb-select" aria-label={d('members_role')} value={m.role} onChange={(e) => void setMember({ user_id: m.user_id, role: e.currentTarget.value as MemberRole })}>
                <option value="admin">{d('role_admin')}</option>
                <option value="staff">{d('role_staff')}</option>
              </select>
              <Button variant="ghost" onClick={() => void remove(m)}>{d('members_remove')}</Button>
            </li>
          ))}
        </ul>
        <form class="dash-inline-form" onSubmit={(e) => {
          e.preventDefault()
          void setMember({ email: email.trim(), role }).then(() => setEmail(''))
        }}>
          <input class="fb-input" type="email" required placeholder={d('members_email')} aria-label={d('members_email')} value={email} onInput={(e) => setEmail(e.currentTarget.value)} data-testid="dash-member-email" />
          <select class="fb-select" aria-label={d('members_role')} value={role} onChange={(e) => setRole(e.currentTarget.value as MemberRole)}>
            <option value="staff">{d('role_staff')}</option>
            <option value="admin">{d('role_admin')}</option>
          </select>
          <Button type="submit" testId="dash-member-add">{d('members_add')}</Button>
        </form>
      </section>
    </div>
  )
}
